import { getWorkbookRepositoryName, isWorkbookWritable } from 'cadenza/workbook/workbook.state';
import { getStore } from 'cadenza/store/cadenza-store';
import type { RepositoryName } from 'cadenza/repository/model/repository-name';
import type { RepositoryDto } from 'cadenza/api-client/dto/repository-dto';
import { repositoryService } from 'cadenza/repository/repository-service';

interface EnrichmentRuleCheckerOptions {
  enrichmentObjecttypeRepositoryHasPublishingEnabled: boolean;
  workbookRepositoryHasPublishingEnabled: boolean;
  workbookAndEnrichmentObjecttypeInTheSameRepository: boolean;
}

export async function getAsyncEnrichmentRuleCheckerOptions(repositoryNameOfEnrichmentObjectType: RepositoryName) {
  const repositories = await repositoryService().list();
  return getSyncEnrichmentRuleCheckerOptions(repositoryNameOfEnrichmentObjectType, repositories);
}

export function getSyncEnrichmentRuleCheckerOptions(
  repositoryNameOfEnrichmentObjectType: RepositoryName,
  repositories: RepositoryDto[],
) {
  const workbookRepositoryName = getWorkbookRepositoryName(getStore().getState());
  const workbookAndEnrichmentObjecttypeInTheSameRepository =
    workbookRepositoryName === repositoryNameOfEnrichmentObjectType;
  const enrichmentObjecttypeRepositoryHasPublishingEnabled =
    repositories.find((repository) => repository.name === repositoryNameOfEnrichmentObjectType)
      ?.publishingProcessEnabled || false;
  const workbookRepositoryHasPublishingEnabled =
    repositories.find((repository) => repository.name === workbookRepositoryName)?.publishingProcessEnabled || false;
  return {
    enrichmentObjecttypeRepositoryHasPublishingEnabled,
    workbookRepositoryHasPublishingEnabled,
    workbookAndEnrichmentObjecttypeInTheSameRepository,
  };
}

/**
 * The following things also are required but already are checked in the backend
 * - 'visible' privilege on the OT to start from
 * - 'write' privilege on the OT to start from, if it is located in a repository with publishing process enabled
 * - 'modifyContent' privilege on the repository of the OT to start from
 * - 'writeData' on the enrichmentOT to rerun the enrichment
 * - and much more... For details please look at
 *   {@link net/disy/cadenza/selfservice/enrichment/service/ObjecttypeEnrichmentFacade.java#isEnrichable}
 *   and {@link net/disy/cadenza/selfservice/enrichment/service/ObjecttypeEnrichmentFacade.java#canRerunEnrichment}
 *
 * Additionally we need to check some things in the frontend here which are connected to the currently opened workbook,
 * because in the backend the currently opened workbook is not present atm.
 *
 * Scenario 1: If the user is currently seeing the published version of a workbook that's in a repository using publishing
 *     Scenario 1.1: The user wants to create an enrichment from an OT that is stored in the same repository as the workbook
 *         Scenario 1.1.1:
 *             AND the user has write privilege to the workbook
 *             THEN the user can create the enrichment
 *             AND an intermediate dialog is shown where the user has to confirm that a new draft version of the workbook is created by performing an intermediate save. See UX.
 *                 Of course - in order to create an enrichment within a workbook additional privileges and prerequisites are needed
 *         Scenario 1.1.2:
 *             AND the user does not have write privilege to the workbook
 *             THEN the user cannot create a the enrichment. The option to create it is hidden.
 *    Scenario 1.2: The user wants to rerun an enrichment
 *         Scenario 1.2.1:
 *             AND the user wants to rerun an enrichment from an enriched OT that is stored in the same repository as the workbook
 *             AND the user has write privilege to the workbook
 *             THEN the user can rerun the enrichment
 *             AND an intermediate dialog is shown where the user has to confirm that a new draft version of the workbook is created by performing an intermediate save. See UX.
 *         Scenario 1.2.2:
 *             AND the user wants to rerun an enrichment from an enriched OT that is stored in the same repository as the workbook
 *             AND the user does not have write privilege to the workbook
 *             THEN the user cannot rerun the enrichment. The option to rerun it is hidden
 * Scenario 2:  If the user is currently seeing a workbook and wants to modify an enrichment from an enriched OT that is stored in another repository
 *     Scenario 2.1: The user wants to create an enrichment
 *         Scenario 2.1.1:
 *             AND the enrichment is from an enriched OT that is stored in a repository with publishing
 *             THEN the user cannot create the enrichment. The option to create it is hidden.
 *    Scenario 2.2: The user wants to rerun an enrichment
 *        Scenario 2.2.1:
 *             AND the enrichment is from an enriched OT that is stored in a repository without publishing
 *             AND has write and writeData privileges on the enrichment OT
 *             THEN the user can rerun the enrichment
 *        Scenario 2.2.2:
 *             AND the enrichment is from an enriched OT that is stored in a repository with publishing
 *             THEN the user cannot rerun the enrichment. The option to rerun it is hidden.
 *
 * @param options The flags indicating the state of the workbook and objecttype
 */
export function areRulesSatisfiedForCreatingAnEnrichment(options: EnrichmentRuleCheckerOptions) {
  // Scenario 1.1 and 1.2:
  if (options.workbookAndEnrichmentObjecttypeInTheSameRepository && options.workbookRepositoryHasPublishingEnabled) {
    // Scenario 1.1.1 (Intermediate dialog is handled outside) and Scenario 1.1.2 and Scenario 1.2.1 and 1.2.2
    return isWorkbookWritable(getStore().getState());
  }
  // Scenario 2.1.1
  return options.workbookAndEnrichmentObjecttypeInTheSameRepository;
}

export function areRulesSatisfiedForRerunningAnEnrichment(options: EnrichmentRuleCheckerOptions) {
  // Scenario 1.1 and 1.2:
  if (options.workbookAndEnrichmentObjecttypeInTheSameRepository && options.workbookRepositoryHasPublishingEnabled) {
    // Scenario 1.1.1 (Intermediate dialog is handled outside) and Scenario 1.1.2 and Scenario 1.2.1 and 1.2.2
    return isWorkbookWritable(getStore().getState());
  }
  const workbookAndObjecttypeInDifferentRepositories = !options.workbookAndEnrichmentObjecttypeInTheSameRepository;
  // 2.2.1 and 2.2.2
  // All privileges on the objecttypes already were checked in the backend before
  // and no workbook related privileges have to be checked here
  return !(workbookAndObjecttypeInDifferentRepositories && options.enrichmentObjecttypeRepositoryHasPublishingEnabled);
}
